/**
 * The task (C8) — one task's whole life on one screen (`KLQ-20260821-001` §F, §G).
 *
 * FIVE DECISIONS THIS FILE IS BUILT ON
 *
 * 1. **The screen computes nothing about roles** (§F). The workflow buttons are exactly
 *    `allowed_transitions` off the payload; the audience panel is `GET /tasks/{id}/audience`;
 *    the one refusal this file cannot know ahead of the act — whether this caller may edit
 *    content at all — is rendered as the server's own sentence when it arrives, never guessed
 *    with a greyed-out control.
 * 2. **A closed task offers no content edit at all.** The server answers `409` ("the closed
 *    record stands") and the screen does not ask: on `closed` every `PATCH` control is
 *    replaced by the read-only fact it would have edited. The thread, the attachments and the
 *    follow button stay — the server accepts those on a closed task, because a record people
 *    keep talking about is still a record.
 * 3. **Reopen is a creation, not an edit.** Offered only on `closed`, answers `201` with a
 *    new task carrying `reopened_from_task_id`. The closed row holds no forward pointer by
 *    design, so the moment after reopening is the one moment this side can render both ends
 *    of the link — which it does, as a row, before the person follows it.
 * 4. **An admission is stated before the act that performs it** (§F). Assigning to somebody
 *    whose firm is not yet in the audience lets that firm in; where this screen can determine
 *    that from what the server told it — the member's company, the audience list, and whether
 *    the caller's own workspace owns the project — the picker interposes the statement and a
 *    confirm. Where it cannot determine it, it stays silent rather than guessing (§F again:
 *    a wrong sentence about access is worse than none), and the audience panel remains the
 *    always-true record after the fact.
 * 5. **Its liveness is not the poll's.** The 30 s version signal moves for the register and
 *    the board and deliberately not for a comment, an attachment or a watcher (§E) — so
 *    nothing here keys off it, and the detail refreshes on the ordinary mount/stale cycle.
 */

import { useRef, useState } from "react";

import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, MapPin } from "lucide-react";

// UX cleanup 2026-08-25: `Input` left with the description's edit form; `InlineEdit` replaced it.
import {
  Button,
  ChipSelect,
  InlineEdit,
  InlineError,
  Pill,
  SectionTitle,
  Select,
  Skeleton,
  SkeletonLines,
} from "@/components";
import type { PillTone } from "@/components";
import { readDownloadUrl, UploadError } from "@/features/drawings";
import {
  membersQueryOptions,
  partiesQueryOptions,
  useCreateParty,
  type ProjectMember,
} from "@/features/people";
import { useWorkspaceFirms } from "@/features/studio";
import { projectQueryOptions } from "@/features/projects";
import { useActiveWorkspaceId } from "@/features/session";
import {
  placeholderLevel,
  renditionQueryOptions,
  versionQueryOptions,
  type PageSize,
} from "@/features/viewer";
import type { MessageKeys, Translate } from "@/lib/i18n";
import { useFormats, useMessages } from "@/lib/i18n";

import type {
  AudienceReason,
  DeliverableStatus,
  Task,
  TaskAdmission,
  TaskPriority,
  TaskStatus,
  TaskUpdate,
} from "./api";
import { TASK_PRIORITIES } from "./api";
import { dayOfDueAt, dueAtEndOfDay } from "./filters";
import { FirmName, firmOf, OffSystemMark } from "./firms";
import { KIND_KEY } from "./kinds";
import { LabelPicker } from "./LabelPicker";
import { MentionPicker } from "./MentionPicker";
import { attachFailureKey, detailOf, taskErrorKey } from "./problems";
import {
  renderPersonName,
  renderPersonNames,
  taskAttachmentsQueryOptions,
  taskAudienceQueryOptions,
  taskCommentsQueryOptions,
  taskDeliverablesQueryOptions,
  taskWatchersQueryOptions,
  useAddTaskAttachment,
  useAddTaskComment,
  useMemberNames,
  useNodeTrail,
  useProjectLabels,
  useReopenTask,
  useSetTaskWatching,
  useTask,
  useTaskTransition,
  useUpdateTask,
} from "./queries";
import { TaskCreateDrawer } from "./TaskCreateDrawer";
import { KindGlyph } from "./TaskRegister";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "status.open",
  in_progress: "status.inProgress",
  done: "status.done",
  closed: "status.closed",
};

/** TWIN: `STATUS_TONE` in `TaskRegister.tsx` — one ramp, drawn by the one `Pill` (§10). */
const STATUS_TONE: Readonly<Record<TaskStatus, PillTone>> = {
  open: "info",
  in_progress: "warning",
  done: "success",
  closed: "neutral",
};

const PRIORITY_KEY: Readonly<Record<TaskPriority, Key>> = {
  high: "priority.high",
  normal: "priority.normal",
  low: "priority.low",
};

/** The verb on each workflow button — a translated act, not an interpolated state name. */
const TRANSITION_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "workflow.toOpen",
  in_progress: "workflow.toInProgress",
  done: "workflow.toDone",
  closed: "workflow.toClosed",
};

const REASON_KEY: Readonly<Record<AudienceReason, Key>> = {
  created_by: "audience.reason.createdBy",
  assigned_to: "audience.reason.assignedTo",
  mentioned: "audience.reason.mentioned",
  shared: "audience.reason.shared",
};

/**
 * The derived deliverable status (`KLQ-20260824-001`) — never stored, never edited here. The
 * ramp borrows the task pills: planned is the low-contrast resting state exactly as open is,
 * and in-progress is the one colour that shouts.
 */
const DELIVERABLE_STATUS_KEY: Readonly<Record<DeliverableStatus, Key>> = {
  planned: "deliverable.status.planned",
  in_progress: "deliverable.status.inProgress",
  done: "deliverable.status.done",
};

const DELIVERABLE_STATUS_TONE: Readonly<Record<DeliverableStatus, PillTone>> = {
  planned: "info",
  in_progress: "warning",
  done: "success",
};

/** The sentence an assignment is about to make true, held while the person decides. */
interface PendingAssignment {
  readonly userId: string;
  readonly personName: string;
  readonly firmName: string;
}

/** A firm pick that would let a tenant in, waiting for the person to confirm it (decision 4). */
interface PendingFirm {
  readonly partyId: string;
  readonly firmName: string;
}

export interface TaskDetailProps {
  readonly projectId: string;
  readonly taskId: string;
}

export function TaskDetail({ projectId, taskId }: TaskDetailProps) {
  const t = useMessages("tasks");
  const formats = useFormats();
  const detail = useTask(projectId, taskId);

  const task = detail.data?.data ?? null;
  const etag = detail.data?.etag ?? null;

  if (detail.isError) {
    return (
      <div className={styles.detail}>
        <BackLink projectId={projectId} t={t} />
        <InlineError variant="block">
          {detailOf(detail.error) ?? t(taskErrorKey(detail.error))}
        </InlineError>
      </div>
    );
  }
  // A task reached under the wrong project's URL is rendered as gone rather than adopted:
  // every lookup on this screen — members, labels, the tree — is keyed by the *route's*
  // project, and dressing another project's task in them would name the wrong people.
  if (task !== null && task.project_id !== projectId) {
    return (
      <div className={styles.detail}>
        <BackLink projectId={projectId} t={t} />
        <InlineError variant="block">{t("error.gone")}</InlineError>
      </div>
    );
  }
  if (task === null) {
    return (
      <div className={styles.detail}>
        <BackLink projectId={projectId} t={t} />
        {/* The two columns are drawn empty rather than skipped: the pane's width is decided by
            the grid, so a placeholder that stood outside it would be one width and the loaded
            detail another. The rail waits — a card there is a section title and a short list,
            neither of which the field rhythm on the left stands for. */}
        <div className={styles.detailColumns}>
          <div className={styles.detailMain}>
            <SkeletonLines label={t("status.loading")} lines={6} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <LoadedTaskDetail
      projectId={projectId}
      taskId={taskId}
      task={task}
      etag={etag}
      t={t}
      formats={formats}
    />
  );
}

function BackLink({
  projectId,
  t,
}: {
  readonly projectId: string;
  readonly t: Translate<"tasks">;
}) {
  return (
    <Link
      to="/projects/$projectId/tasks"
      params={{ projectId }}
      search={{}}
      className={styles.backLink}
    >
      <ArrowLeft size={14} strokeWidth={1.5} aria-hidden /> {t("detail.back")}
    </Link>
  );
}

/**
 * The screen once the task is in hand. A separate component so every hook below runs
 * unconditionally against a real row — the loading and error shells above return early.
 */
function LoadedTaskDetail({
  projectId,
  taskId,
  task,
  etag,
  t,
  formats,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly task: Task;
  readonly etag: string | null;
  readonly t: Translate<"tasks">;
  readonly formats: ReturnType<typeof useFormats>;
}) {
  const nameOf = useMemberNames(projectId);
  const trailOf = useNodeTrail(projectId);
  const labels = useProjectLabels(projectId);
  const members = useQuery(membersQueryOptions(projectId));
  const parties = useQuery(partiesQueryOptions(projectId));
  /**
   * **Add from the studio's directory, without leaving the task** (owner, 2026-09-16).
   *
   * The picker still offers the project's roster and nothing else — assignment means *this firm
   * is on this project*, and a dropdown that quietly grew the roster would make that sentence
   * false behind somebody's back. So a firm the studio knows but this project does not is a
   * **second step, stated**: it joins the roster and takes the task in one gesture the person
   * chose, which is the *roster + add* shape the owner picked over the silent one.
   */
  const directory = useWorkspaceFirms();
  const addParty = useCreateParty(projectId);
  const project = useQuery(projectQueryOptions(projectId));
  const audience = useQuery(taskAudienceQueryOptions(projectId, taskId));
  const myWorkspaceId = useActiveWorkspaceId();

  const update = useUpdateTask(projectId, taskId);
  const transition = useTaskTransition(projectId, taskId);
  const reopen = useReopenTask(projectId, taskId);

  // The thumbnail's chain: the version the pin was placed on, its rendition, and the
  // single-tile placeholder. All three are already-cached queries; no new endpoint.
  const sourceVersion = useQuery(
    versionQueryOptions(projectId, task.source_version_id),
  );
  const renditionBlobId =
    sourceVersion.data?.files?.find((file) => file.kind === "rendition_pdf")
      ?.blob_ref_id ?? null;
  const sourceRendition = useQuery(
    renditionQueryOptions(projectId, renditionBlobId),
  );

  const [pendingAssignment, setPendingAssignment] =
    useState<PendingAssignment | null>(null);
  const [pendingFirm, setPendingFirm] = useState<PendingFirm | null>(null);
  // UX cleanup 2026-08-25 — the edit-mode state, and with it the Edit, Save and Cancel
  // buttons. The title and the description are `InlineEdit` fields now: Enter or clicking
  // away saves, Escape puts the old value back. Restore this line with the form.
  // const [editing, setEditing] = useState<{ title: string; description: string } | null>(null);

  const closed = task.status === "closed";
  // Every content edit rides on the validator the read carried; without one the control
  // would have to invent `If-Match: *`, which matches anything and is exactly the
  // last-writer-wins the header exists to prevent.
  const editable = !closed && etag !== null;

  const patch = (changes: Partial<TaskUpdate>, onSaved?: () => void): void => {
    if (etag === null) return;
    // The clear flags are required on the wire (`false` is "leave it alone"); every call site
    // below names only the field it changes and inherits the no-op for the rest. A flag rather
    // than a nullable value because a scalar cannot say "set it to nobody" and "do not touch
    // it" with one value — `assignee_user_ids` needs none, since `[]` is a legal set.
    update.mutate(
      {
        changes: {
          clear_due_at: false,
          clear_assignee_party: false,
          ...changes,
        },
        etag,
      },
      onSaved === undefined ? {} : { onSuccess: onSaved },
    );
  };

  /**
   * Who owns this project, as far as this caller can tell — `null` when they are looking in
   * from outside, because an external caller is told nothing about the owner and a screen that
   * cannot tell inside from outside marks nobody (decision 4). Hoisted out of `admissionFor`
   * on 2026-09-15: the mention picker asks the same question of the same value, and two
   * copies of "who owns this" is the kind of pair that drifts.
   */
  const owningWorkspaceId =
    project.data?.relation === "owned" ? myWorkspaceId : null;

  /**
   * Decision 4: whether assigning this member would let their firm in, answered only from
   * facts the server already gave this caller. `null` means "no admission this screen can
   * name" — the member is internal, their firm is already in the audience, they have no
   * claimed firm to admit, or the caller is external and cannot know who the owner is.
   */
  const admissionFor = (userId: string): PendingAssignment | null => {
    if (owningWorkspaceId === null) return null;
    const member = members.data?.items.find((row) => row.user_id === userId);
    const firmId = member?.company_workspace_id ?? null;
    if (member === undefined || firmId === null || firmId === owningWorkspaceId)
      return null;
    if (audience.data === undefined) return null;
    if (
      audience.data.items.some(
        (entry) => entry.audience_workspace_id === firmId,
      )
    )
      return null;
    return {
      userId,
      personName:
        member.display_name ?? member.email ?? t("person.unavailable"),
      firmName: member.company_name ?? t("audience.firmUnknown"),
    };
  };

  // The wire takes the whole set (`KLQ-20260824-001`, the `label_ids` idiom): `[]` unassigns
  // everybody, an absent field leaves the set alone. Both acts below send the full next set,
  // so a stale read loses to the `If-Match` rather than to a partial diff.
  const addAssignee = (userId: string): void => {
    setPendingAssignment(null);
    patch({ assignee_user_ids: [...task.assignee_user_ids, userId] });
  };

  const removeAssignee = (userId: string): void => {
    patch({
      assignee_user_ids: task.assignee_user_ids.filter(
        (held) => held !== userId,
      ),
    });
  };

  /**
   * The firm that owes this task — a second answer beside the people, never a spelling of the
   * same one (`KLQ-20260820-007`). Setting a firm leaves the people alone and the reverse: a
   * firm with nobody on it is *Needs an owner*, which is a different problem from *Unassigned*.
   *
   * A firm that **is** a tenant (`workspace_ref_id` set) is admitted by the same write
   * (`tasks/service.py::update_task`, `_admit`), so the pick states that before performing it —
   * decision 4, the same interposed sentence the people picker uses. An off-system firm admits
   * nobody and notifies nobody, and goes straight through.
   */
  const assignFirm = (partyId: string): void => {
    setPendingFirm(null);
    patch({ assignee_party_id: partyId });
  };

  const pickFirm = (partyId: string): void => {
    // A directory firm arrives prefixed, because it is not a roster row yet: it joins the
    // roster first and the ordinary path takes it from there.
    if (partyId.startsWith("firm:")) {
      addFromDirectory(partyId.slice("firm:".length));
      return;
    }
    if (partyId === "") {
      setPendingFirm(null);
      if (task.assignee_party_id !== null)
        patch({ clear_assignee_party: true });
      return;
    }
    const party = parties.data?.find((row) => row.id === partyId);
    const tenant = party?.workspace_ref_id ?? null;
    const admits =
      party !== undefined &&
      owningWorkspaceId !== null &&
      tenant !== null &&
      tenant !== owningWorkspaceId &&
      audience.data !== undefined &&
      !audience.data.items.some(
        (entry) => entry.audience_workspace_id === tenant,
      );
    if (admits) setPendingFirm({ partyId, firmName: party.name });
    else assignFirm(partyId);
  };

  /** A directory firm this project has no roster row for yet. The value is the *firm* id. */
  const offRoster = (directory.data ?? []).filter(
    (one) =>
      one.archived_at === null &&
      !(parties.data ?? []).some((party) => party.firm_id === one.id),
  );

  const addFromDirectory = (firmId: string): void => {
    addParty.mutate(
      { firm_id: firmId, note: null },
      {
        onSuccess: (party) => {
          // The roster row exists now, so the ordinary path decides the rest — including the
          // admission sentence for a firm that turns out to be a tenant.
          pickFirm(party.id);
        },
      },
    );
  };

  const firm = firmOf(parties.data, task.assignee_party_id);
  const assignableFirms = parties.data ?? [];
  // A firm archived after it was given this task has left the roster read, but it still owes
  // the work — the select keeps it as its current value under the neutral label rather than
  // silently showing *nobody*.
  const heldFirmMissing =
    task.assignee_party_id !== null &&
    !assignableFirms.some((row) => row.id === task.assignee_party_id);

  const trail = trailOf(task.hierarchy_node_id);
  const slot = trail.at(-1)?.kind === "slot" ? trail.at(-1) : undefined;
  const labelName = (id: string): string | null =>
    labels.data?.items.find((label) => label.id === id)?.name ?? null;
  const labelNames = task.label_ids
    .map(labelName)
    .filter((name) => name !== null);

  const raisedBy = renderPersonName(
    t,
    nameOf(task.created_by_user_id),
    t("person.unavailable"),
  );
  const assignees = renderPersonNames(
    t,
    nameOf,
    task.assignee_user_ids,
    t("row.unassigned"),
  );

  return (
    <div className={styles.detail}>
      <BackLink projectId={projectId} t={t} />

      <header className={styles.detailHeader}>
        <div className={styles.titleRow}>
          <KindGlyph
            kind={task.kind}
            label={t(KIND_KEY[task.kind])}
            taskTypeId={task.task_type_id}
          />
          <h2 className={styles.detailTitle}>
            <span className={styles.number}>
              {t("row.number", { number: task.number })}
            </span>{" "}
            {/* The heading is the field (UX cleanup, 2026-08-25). A closed task, or one read
                without a validator, gets the plain text — the same rule `editable` has always
                carried, applied here rather than to a button that used to open a form. */}
            {editable ? (
              <InlineEdit
                label={t("detail.titleLabel")}
                value={task.title}
                variant="title"
                required
                maxLength={200}
                saving={update.isPending}
                onCommit={(next) => {
                  patch({ title: next });
                }}
              />
            ) : (
              task.title
            )}
          </h2>
          <Pill tone={STATUS_TONE[task.status]}>
            {t(STATUS_KEY[task.status])}
          </Pill>
        </div>
        <p className={styles.rowMeta}>
          {t("detail.raisedBy", {
            name: raisedBy,
            when: formats.moment(task.created_at),
          })}
          {task.closed_at !== null
            ? ` · ${t("detail.closedBy", {
                name: renderPersonName(
                  t,
                  nameOf(task.closed_by_user_id),
                  t("person.unavailable"),
                ),
                when: formats.moment(task.closed_at),
              })}`
            : ""}
        </p>
      </header>

      {/* Decision 1: the buttons are the payload's `allowed_transitions`, nothing else. A
          closed task has none, so this row simply is not there — reopen is its own act. */}
      {task.allowed_transitions.length > 0 ? (
        <div
          className={styles.workflow}
          role="group"
          aria-label={t("workflow.legend")}
        >
          {task.allowed_transitions.map((to) => (
            <Button
              key={to}
              size="sm"
              variant="secondary"
              disabled={transition.isPending}
              onClick={() => {
                transition.mutate(to);
              }}
            >
              {t(TRANSITION_KEY[to])}
            </Button>
          ))}
        </div>
      ) : null}
      {transition.isError ? (
        <InlineError variant="block">
          {detailOf(transition.error) ?? t(taskErrorKey(transition.error))}
        </InlineError>
      ) : null}

      {closed ? (
        <ReopenBlock projectId={projectId} reopen={reopen} t={t} />
      ) : null}
      {task.reopened_from_task_id !== null ? (
        <OriginBlock
          projectId={projectId}
          originId={task.reopened_from_task_id}
          t={t}
        />
      ) : null}

      <div className={styles.detailColumns}>
        <div className={styles.detailMain}>
          {/* Decision 2: on `closed` this whole panel is the read-only facts. */}
          <section className={styles.section}>
            <div className={styles.metaGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  {t("filter.assignee")}
                </span>
                {editable ? (
                  /* The set and its adder are one control (`ChipSelect`): a chip per person,
                     each its own removal — assignees are equals (`KLQ-20260824-001`
                     decision 3), so there is no primary slot to argue over — and the picker
                     inside the same box. A departed assignee renders as the em dash and stays
                     removable: leaving the project did not unassign them.

                     The picker is an adder, not a state, and until the audience is in hand it
                     cannot state an admission it is about to perform, so it does not offer
                     the act (decision 4). */
                  <ChipSelect
                    tokens={task.assignee_user_ids.map((userId) => ({
                      id: userId,
                      label: renderPersonName(
                        t,
                        nameOf(userId),
                        t("person.unavailable"),
                      ),
                    }))}
                    options={(members.data?.items ?? [])
                      .filter(
                        (member) =>
                          !task.assignee_user_ids.includes(member.user_id),
                      )
                      .map((member) => ({
                        id: member.user_id,
                        label:
                          member.display_name ??
                          member.email ??
                          t("person.unavailable"),
                      }))}
                    addLabel={t("assign.add")}
                    removeLabel={(name) => t("assign.remove", { name })}
                    disabled={update.isPending}
                    addDisabled={audience.isPending}
                    onAdd={(userId) => {
                      const admission = admissionFor(userId);
                      if (admission === null) addAssignee(userId);
                      else setPendingAssignment(admission);
                    }}
                    onRemove={removeAssignee}
                  />
                ) : (
                  <span className={styles.metaValue}>{assignees}</span>
                )}
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t("assign.firm")}</span>
                {editable ? (
                  <div className={styles.firmField}>
                    {/* A native `<option>` carries text and nothing else, so the off-system
                        mark rides inside the option's own label while choosing
                        (`assign.firmOption`) and beside the select once chosen. */}
                    <Select
                      label={t("assign.firm")}
                      labelHidden
                      size="sm"
                      value={task.assignee_party_id ?? ""}
                      disabled={
                        update.isPending ||
                        audience.isPending ||
                        parties.isPending
                      }
                      onChange={(event) => {
                        pickFirm(event.target.value);
                      }}
                    >
                      <option value="">{t("assign.firmNone")}</option>
                      {heldFirmMissing && task.assignee_party_id !== null ? (
                        <option value={task.assignee_party_id}>
                          {t("assign.firmUnknown")}
                        </option>
                      ) : null}
                      {assignableFirms.map((party) => (
                        <option key={party.id} value={party.id}>
                          {(party.workspace_ref_id ?? null) === null
                            ? t("assign.firmOption", { name: party.name })
                            : party.name}
                        </option>
                      ))}
                      {offRoster.length === 0 ? null : (
                        <optgroup label={t("assign.firmDirectory")}>
                          {offRoster.map((one) => (
                            <option key={one.id} value={`firm:${one.id}`}>
                              {one.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </Select>
                    {firm?.offSystem === true ? <OffSystemMark t={t} /> : null}
                  </div>
                ) : (
                  <span className={styles.metaValue}>
                    <FirmName firm={firm} t={t} />
                  </span>
                )}
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  {t("filter.priority")}
                </span>
                {editable ? (
                  <Select
                    label={t("filter.priority")}
                    labelHidden
                    size="sm"
                    value={task.priority}
                    disabled={update.isPending}
                    onChange={(event) => {
                      patch({ priority: event.target.value as TaskPriority });
                    }}
                  >
                    {TASK_PRIORITIES.map((priority) => (
                      <option key={priority} value={priority}>
                        {t(PRIORITY_KEY[priority])}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className={styles.metaValue}>
                    {t(PRIORITY_KEY[task.priority])}
                  </span>
                )}
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t("detail.due")}</span>
                {editable ? (
                  <input
                    type="date"
                    className={styles.select}
                    value={dayOfDueAt(task.due_at)}
                    disabled={update.isPending}
                    onChange={(event) => {
                      const instant = dueAtEndOfDay(event.target.value);
                      patch(
                        instant === null
                          ? { clear_due_at: true }
                          : { due_at: instant },
                      );
                    }}
                  />
                ) : (
                  <span className={styles.metaValue}>
                    {task.due_at === null
                      ? t("row.noDue")
                      : formats.date(task.due_at)}
                  </span>
                )}
              </div>
            </div>

            {pendingFirm !== null ? (
              <div className={styles.notice}>
                <p className={styles.noticeText}>
                  {t("assign.firmAdmits", { firm: pendingFirm.firmName })}
                </p>
                <div className={styles.noticeActions}>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={update.isPending}
                    onClick={() => {
                      assignFirm(pendingFirm.partyId);
                    }}
                  >
                    {t("assign.confirm")}
                  </Button>
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={() => {
                      setPendingFirm(null);
                    }}
                  >
                    {t("label.cancel")}
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Decision 4's interposed sentence: the act waits for the person. */}
            {pendingAssignment !== null ? (
              <div className={styles.notice}>
                <p className={styles.noticeText}>
                  {t("assign.admits", {
                    name: pendingAssignment.personName,
                    firm: pendingAssignment.firmName,
                  })}
                </p>
                <div className={styles.noticeActions}>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={update.isPending}
                    onClick={() => {
                      addAssignee(pendingAssignment.userId);
                    }}
                  >
                    {t("assign.confirm")}
                  </Button>
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={() => {
                      setPendingAssignment(null);
                    }}
                  >
                    {t("label.cancel")}
                  </Button>
                </div>
              </div>
            ) : null}

            {editable ? (
              <LabelPicker
                projectId={projectId}
                labels={task.label_ids}
                onChange={(next) => {
                  patch({ label_ids: [...next] });
                }}
              />
            ) : labelNames.length > 0 ? (
              <p className={styles.rowMeta}>
                <span className={styles.fieldLabel}>{t("label.legend")}</span>{" "}
                {labelNames.join(", ")}
              </p>
            ) : null}

            {task.requires_verification ? (
              <p className={styles.muted}>{t("detail.verification")}</p>
            ) : null}
            {closed ? (
              <p className={styles.muted}>{t("detail.closedRecord")}</p>
            ) : null}
            {update.isError ? (
              <InlineError variant="block">
                {detailOf(update.error) ?? t(taskErrorKey(update.error))}
              </InlineError>
            ) : null}
          </section>

          <section className={styles.section}>
            <SectionTitle as="h3">{t("detail.location")}</SectionTitle>
            {task.hierarchy_node_id === null ? (
              <p className={styles.muted}>{t("detail.noLocation")}</p>
            ) : trail.length === 0 ? (
              // The caller's tree does not hold this node — a scoped member reading a task
              // admitted to them from outside their zones. Saying so beats a wrong address.
              <p className={styles.muted}>{t("detail.locationUnknown")}</p>
            ) : (
              <p className={styles.crumbs}>
                {trail.map((node) => node.name).join(" › ")}
              </p>
            )}
            {slot !== undefined ? (
              <>
                {task.anchor !== null && task.source_version_id !== null ? (
                  <DrawingThumbnail
                    anchor={task.anchor}
                    rendition={sourceRendition.data ?? null}
                    loading={
                      sourceVersion.isPending ||
                      (renditionBlobId !== null && sourceRendition.isPending)
                    }
                    alt={t("detail.thumbnailAlt", { name: slot.name })}
                    projectId={projectId}
                    slotId={slot.id}
                    versionId={task.source_version_id}
                  />
                ) : null}
                <p className={styles.drawingLine}>
                  {task.anchor !== null ? (
                    <>
                      <MapPin
                        className={styles.glyph}
                        size={14}
                        strokeWidth={1.5}
                        role="img"
                        aria-label={t("row.pinned")}
                      />{" "}
                      {t("detail.pin", {
                        x: Math.round(task.anchor.x * 100),
                        y: Math.round(task.anchor.y * 100),
                      })}{" "}
                    </>
                  ) : null}
                  <Link
                    to="/projects/$projectId/drawings/$slotId"
                    params={{ projectId, slotId: slot.id }}
                    search={
                      task.source_version_id === null
                        ? {}
                        : { rev: task.source_version_id }
                    }
                    className={styles.linkButton}
                  >
                    {t("detail.openDrawing", { name: slot.name })}
                  </Link>
                </p>
              </>
            ) : null}
          </section>

          <Deliverables projectId={projectId} taskId={taskId} t={t} />

          <SubtaskSection
            projectId={projectId}
            task={task}
            closed={closed}
            t={t}
          />

          <DescriptionSection
            task={task}
            editable={editable}
            patch={patch}
            saving={update.isPending}
            t={t}
          />

          {/* The members list and the owner are this component's already — the thread's
              mention picker and its admission confirmation both read them, and a second
              `useQuery` down there would be a second place for the same answer to be
              pending. */}
          <Thread
            projectId={projectId}
            taskId={taskId}
            nameOf={nameOf}
            members={members.data?.items ?? []}
            membersPending={members.isPending}
            owningWorkspaceId={owningWorkspaceId}
            t={t}
            formats={formats}
          />

          <Attachments
            projectId={projectId}
            taskId={taskId}
            nameOf={nameOf}
            t={t}
            formats={formats}
          />
        </div>

        <aside className={styles.detailRail}>
          <Watchers
            projectId={projectId}
            taskId={taskId}
            nameOf={nameOf}
            t={t}
          />
          <Audience
            projectId={projectId}
            taskId={taskId}
            members={members.data?.items ?? []}
            t={t}
            formats={formats}
          />
        </aside>
      </div>
    </div>
  );
}

/**
 * Decision 3's acting half. The button is offered on every closed task — who may reopen is
 * the server's call, and its `403` is rendered rather than predicted. The `201` row is
 * rendered in place, as the one moment both ends of the link are in this side's hands.
 */
function ReopenBlock({
  projectId,
  reopen,
  t,
}: {
  readonly projectId: string;
  readonly reopen: ReturnType<typeof useReopenTask>;
  readonly t: Translate<"tasks">;
}) {
  const created = reopen.data ?? null;
  return (
    <div className={styles.reopenBlock}>
      {created === null ? (
        <>
          <p className={styles.muted}>{t("reopen.hint")}</p>
          <Button
            size="sm"
            variant="secondary"
            loading={reopen.isPending}
            onClick={() => {
              reopen.mutate();
            }}
          >
            {t("reopen.action")}
          </Button>
        </>
      ) : (
        <p className={styles.rowMeta}>
          {t("reopen.created")}{" "}
          <Link
            to="/projects/$projectId/tasks/$taskId"
            params={{ projectId, taskId: created.id }}
            className={styles.linkButton}
          >
            {t("row.number", { number: created.number })} {created.title}
          </Link>
        </p>
      )}
      {reopen.isError ? (
        <InlineError variant="block">
          {detailOf(reopen.error) ?? t(taskErrorKey(reopen.error))}
        </InlineError>
      ) : null}
    </div>
  );
}

/**
 * The back half of the link a reopen created. The fact of the chain is on the payload and is
 * always stated; the *row* — number and title — renders only when the origin loads, because a
 * caller admitted to the new task was deliberately not admitted to the old thread and its
 * `404` is not this screen's to paper over.
 */
function OriginBlock({
  projectId,
  originId,
  t,
}: {
  readonly projectId: string;
  readonly originId: string;
  readonly t: Translate<"tasks">;
}) {
  const origin = useTask(projectId, originId);
  const task = origin.data?.data ?? null;
  return (
    <p className={styles.rowMeta}>
      {t("reopen.origin")}{" "}
      {task === null ? (
        <span className={styles.muted}>{t("reopen.originUnavailable")}</span>
      ) : (
        <Link
          to="/projects/$projectId/tasks/$taskId"
          params={{ projectId, taskId: originId }}
          className={styles.linkButton}
        >
          {t("row.number", { number: task.number })} {task.title}
        </Link>
      )}
    </p>
  );
}

/**
 * The drawing's thumbnail with the pin's position marked.
 *
 * The image is the rendition's deepest single-tile level — the same placeholder the canvas
 * paints first — so it costs one tile and no pyramid walk. The pin is a normalised dot:
 * `TaskAnchor` carries `x` and `y` as fractions of the sheet, and since 2026-09-25 a `page`
 * as well — so the thumbnail is of **the page the pin is on**, not always the first. A
 * thumbnail of page 1 with a page-3 pin drawn on it is a picture of the wrong place.
 *
 * While the chain is in flight the box is known and the bytes are not: a skeleton. Once the
 * rendition has settled to a non-ready state, nothing is rendered — the coordinate line and
 * the text link already stand.
 */
function DrawingThumbnail({
  anchor,
  rendition,
  loading,
  alt,
  projectId,
  slotId,
  versionId,
}: {
  readonly anchor: {
    readonly x: number;
    readonly y: number;
    readonly page: number;
  };
  readonly rendition: {
    readonly state: string;
    readonly pages?: readonly PageSize[] | undefined;
    readonly tile_size?: number | null | undefined;
    readonly bootstrap_tiles?:
      | readonly {
          readonly page: number;
          readonly level: number;
          readonly url: string;
        }[]
      | undefined;
  } | null;
  readonly loading: boolean;
  readonly alt: string;
  readonly projectId: string;
  readonly slotId: string;
  readonly versionId: string;
}) {
  const link = (
    <Link
      to="/projects/$projectId/drawings/$slotId"
      params={{ projectId, slotId }}
      search={{ rev: versionId }}
      className={styles.thumbnailLink}
    >
      {loading ? (
        <div className={styles.thumbnail}>
          <Skeleton width={96} height={68} />
        </div>
      ) : null}
      {!loading && rendition !== null && rendition.state === "ready" ? (
        <ThumbnailImage rendition={rendition} anchor={anchor} alt={alt} />
      ) : null}
    </Link>
  );

  // Not ready and not loading: the coordinate line and the text link already stand.
  if (!loading && rendition?.state !== "ready") return null;
  return link;
}

/** Resolves the single-tile placeholder and renders it with the pin dot. Returns `null` when
    the geometry or the tile is absent — a malformed row is not a thumbnail. */
function ThumbnailImage({
  rendition,
  anchor,
  alt,
}: {
  readonly rendition: {
    readonly pages?: readonly PageSize[] | undefined;
    readonly tile_size?: number | null | undefined;
    readonly bootstrap_tiles?:
      | readonly {
          readonly page: number;
          readonly level: number;
          readonly url: string;
        }[]
      | undefined;
  };
  readonly anchor: {
    readonly x: number;
    readonly y: number;
    readonly page: number;
  };
  readonly alt: string;
}) {
  // The pin's own page, and `undefined` when the rendition has fewer pages than the anchor
  // names — which renders nothing rather than drawing the pin over another sheet. The server
  // deliberately does not validate a pin against `page_count` (`CoordinateAnchor`), so this
  // is the case that reaches the screen.
  const page = rendition.pages?.[anchor.page];
  if (page === undefined) return null;
  const level = placeholderLevel(page, rendition.tile_size ?? 512);
  if (level === null) return null;
  const tile = rendition.bootstrap_tiles?.find(
    (t) => t.page === anchor.page && t.level === level,
  );
  if (tile === undefined) return null;

  return (
    // The box takes the sheet's own proportions, so the whole sheet is drawn and the pin's
    // percentages land where they point. A fixed box with `object-fit: cover` crops a portrait
    // sheet, and a crop moves every pin off its mark.
    <div
      className={styles.thumbnail}
      style={{
        aspectRatio: `${String(page.width)} / ${String(page.height)}`,
        height: "auto",
      }}
    >
      <img src={tile.url} alt={alt} className={styles.thumbnailImage} />
      <span
        className={styles.thumbnailPin}
        style={{
          left: `${String(anchor.x * 100)}%`,
          top: `${String(anchor.y * 100)}%`,
        }}
        aria-hidden
      />
    </div>
  );
}

/**
 * The Deliverables section (`KLQ-20260824-001`) — the drawings this task owns, each with its
 * address, its version state and its derived status.
 *
 * The list arrives already cut to the caller's zone scope; a drawing outside their zones is
 * absent here exactly as it is absent from the tree, and this side neither filters nor greys.
 * Ownership is a record, so a closed task still lists its outputs — the section renders on
 * every task, and "owns no drawings" is a fact worth a sentence rather than a hidden block.
 * The version is stated as presence: the payload carries an id, and printing an id where a
 * person expects a version letter would read as a bug (§G's reasoning, applied to slots).
 */
function Deliverables({
  projectId,
  taskId,
  t,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly t: Translate<"tasks">;
}) {
  const deliverables = useQuery(
    taskDeliverablesQueryOptions(projectId, taskId),
  );

  return (
    <section className={styles.section}>
      <SectionTitle as="h3">{t("deliverable.title")}</SectionTitle>
      {deliverables.isError ? (
        <InlineError variant="block">
          {detailOf(deliverables.error) ?? t(taskErrorKey(deliverables.error))}
        </InlineError>
      ) : deliverables.isPending ? (
        <div className={styles.loadingBlock}>
          <SkeletonLines label={t("status.loading")} lines={2} />
        </div>
      ) : deliverables.data.items.length === 0 ? (
        <p className={styles.muted}>{t("deliverable.none")}</p>
      ) : (
        <ul className={styles.attachmentList}>
          {deliverables.data.items.map((slot) => (
            <li key={slot.id} className={styles.attachmentRow}>
              {/* The identifier is the jump: C1a's viewer route, addressed by the slot the
                  deliverable is. The identifier grammar is the tree's and never the work's,
                  so the text is printed verbatim off the wire. */}
              <Link
                to="/projects/$projectId/drawings/$slotId"
                params={{ projectId, slotId: slot.id }}
                search={{}}
                className={styles.linkButton}
              >
                {slot.slot_identifier}
              </Link>
              <span className={styles.rowMeta}>
                {slot.title === null ? "" : `${slot.title} · `}
                {slot.current_version_id === null
                  ? t("deliverable.noVersion")
                  : t("deliverable.hasVersion")}
              </span>
              <Pill tone={DELIVERABLE_STATUS_TONE[slot.status]}>
                {t(DELIVERABLE_STATUS_KEY[slot.status])}
              </Pill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The subtask entrance (`KLQ-20260824-001` decision 3). A subtask is its own task, anchored
 * at this task's place — there is no `parent_task_id`, by design — so the entrance is the
 * ordinary create drawer with the location pre-filled, and its single-assignee control is the
 * convention that names one accountable person. The schema does not enforce the convention;
 * this drawer holding to it is the enforcement the spec asks of the UI.
 *
 * Not offered on a closed task: raising follow-up work on a closed record is what reopen is
 * for, and offering both side by side would make the terminal state look editable.
 */
function SubtaskSection({
  projectId,
  task,
  closed,
  t,
}: {
  readonly projectId: string;
  readonly task: Task;
  readonly closed: boolean;
  readonly t: Translate<"tasks">;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle as="h3">{t("subtask.title")}</SectionTitle>
        {!closed ? (
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => {
              setCreating(true);
            }}
          >
            {t("subtask.add")}
          </Button>
        ) : null}
      </div>
      <p className={styles.muted}>{t("subtask.hint")}</p>
      {creating ? (
        <TaskCreateDrawer
          projectId={projectId}
          locationId={task.hierarchy_node_id}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </section>
  );
}

function DescriptionSection({
  task,
  editable,
  patch,
  saving,
  t,
}: {
  readonly task: Task;
  readonly editable: boolean;
  readonly patch: (changes: Partial<TaskUpdate>, onSaved?: () => void) => void;
  readonly saving: boolean;
  readonly t: Translate<"tasks">;
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle as="h3">{t("detail.description")}</SectionTitle>
      </div>
      {/* UX cleanup 2026-08-25 — one field where there were three buttons and a form.
          The description is sent as typed: the empty string is how this field is cleared
          (`TaskUpdateRequest` — `null` means "leave it alone" here, and the explicit clear
          flags belong to assignee and due date alone), so it is deliberately *not* `required`.
          A failed save leaves the text on screen, because the field never closed. */}
      {editable ? (
        <InlineEdit
          label={t("detail.description")}
          value={task.description ?? ""}
          multiline
          rows={5}
          maxLength={10_000}
          placeholder={t("detail.noDescription")}
          saving={saving}
          onCommit={(next) => {
            patch({ description: next });
          }}
        />
      ) : task.description === null || task.description === "" ? (
        <p className={styles.muted}>{t("detail.noDescription")}</p>
      ) : (
        <p className={styles.longText}>{task.description}</p>
      )}
    </section>
  );
}

/**
 * The thread, as the plain list the server answers — already cut at the caller's admission,
 * so nothing here filters it. The composer *can* name somebody now (`MentionPicker`): a
 * mention admits the named person's firm, which is why the picker states that before it adds
 * anybody and why the confirmation below states it again, as fact, after the post returns.
 */
function Thread({
  projectId,
  taskId,
  nameOf,
  members,
  membersPending,
  owningWorkspaceId,
  t,
  formats,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly nameOf: ReturnType<typeof useMemberNames>;
  readonly members: readonly ProjectMember[];
  readonly membersPending: boolean;
  readonly owningWorkspaceId: string | null;
  readonly t: Translate<"tasks">;
  readonly formats: ReturnType<typeof useFormats>;
}) {
  const comments = useQuery(taskCommentsQueryOptions(projectId, taskId));
  const post = useAddTaskComment(projectId, taskId);
  const [draft, setDraft] = useState("");
  // Who this draft names. It belongs to the draft, not to the task, so it is cleared with the
  // textarea the moment the post lands — a mention left behind would ride the *next* comment
  // and admit a firm nobody meant to admit twice.
  const [mentions, setMentions] = useState<readonly string[]>([]);
  // The admissions confirmation is tied to the most recent successful post. It is held in
  // local state rather than read off `post.data` directly, because `post.data` persists until
  // the next mutation and the confirmation should disappear as soon as the user starts a new
  // comment — a stale "firm X was let in" under a fresh empty textarea reads as a bug.
  const [lastAdmissions, setLastAdmissions] = useState<
    readonly TaskAdmission[]
  >([]);

  return (
    <section className={styles.section}>
      <SectionTitle as="h3">{t("thread.title")}</SectionTitle>
      {comments.isError ? (
        <InlineError variant="block">
          {detailOf(comments.error) ?? t(taskErrorKey(comments.error))}
        </InlineError>
      ) : comments.isPending ? (
        <div className={styles.loadingBlock}>
          <SkeletonLines label={t("status.loading")} lines={3} />
        </div>
      ) : comments.data.items.length === 0 ? (
        <p className={styles.muted}>{t("thread.empty")}</p>
      ) : (
        <ol className={styles.thread}>
          {comments.data.items.map((comment) => (
            <li key={comment.id} className={styles.comment}>
              <p className={styles.commentMeta}>
                {renderPersonName(
                  t,
                  nameOf(comment.author_user_id),
                  t("person.unavailable"),
                )}
                {" · "}
                {formats.moment(comment.created_at)}
              </p>
              <p className={styles.longText}>{comment.body}</p>
            </li>
          ))}
        </ol>
      )}

      <form
        className={styles.composer}
        onSubmit={(event) => {
          event.preventDefault();
          const body = draft.trim();
          if (body === "") return;
          post.mutate(
            { body, mentionsUserIds: mentions },
            {
              onSuccess: (comment) => {
                setDraft("");
                setMentions([]);
                setLastAdmissions(comment.admissions ?? []);
              },
            },
          );
        }}
      >
        <textarea
          className={styles.textarea}
          value={draft}
          maxLength={10_000}
          rows={3}
          aria-label={t("thread.placeholder")}
          placeholder={t("thread.placeholder")}
          onChange={(event) => {
            setDraft(event.target.value);
            // Starting a new comment clears the previous post's confirmation.
            if (event.target.value !== "") setLastAdmissions([]);
          }}
        />
        <MentionPicker
          members={members}
          owningWorkspaceId={owningWorkspaceId}
          mentions={mentions}
          disabled={post.isPending}
          onAdd={(userId) => {
            setMentions((held) =>
              held.includes(userId) ? held : [...held, userId],
            );
          }}
          onRemove={(userId) => {
            setMentions((held) => held.filter((id) => id !== userId));
          }}
          t={t}
        />
        <Button
          size="sm"
          type="submit"
          variant="primary"
          loading={post.isPending}
        >
          {t("thread.send")}
        </Button>
      </form>
      {/* The confirmation waits for the members query: the firm name resolves through
          `members`, and rendering before it loads would print `firmUnknown` for a name about
          to arrive. A failed members query still renders it, with `firmUnknown` —
          an admission is the one consequence this thread must never keep quiet about (§F). */}
      {lastAdmissions.length > 0 && !membersPending ? (
        <div className={styles.threadAdmitted}>
          {lastAdmissions.map((admission) => {
            const firm =
              members.find(
                (row) =>
                  row.company_workspace_id === admission.audience_workspace_id,
              )?.company_name ?? t("audience.firmUnknown");
            return (
              <p
                key={admission.audience_workspace_id}
                className={styles.threadAdmittedText}
              >
                {t("thread.admitted", {
                  firm,
                  count: admission.earlier_comments_stay_internal,
                })}
              </p>
            );
          })}
        </div>
      ) : null}
      {post.isError ? (
        <InlineError variant="block">
          {detailOf(post.error) ?? t(taskErrorKey(post.error))}
        </InlineError>
      ) : null}
    </section>
  );
}

/**
 * Attachments, and the ticket-flow upload that adds one (`features/drawings/upload.ts`).
 *
 * The idempotency key belongs to *one chosen file*: minted when the file is picked, held for
 * as long as that file is the chosen one, replayed by the retry button — so a retry after a
 * failed transfer resumes the same staging object instead of minting a second. That is the
 * upload contract, restated at the one place this screen mints keys.
 */
function Attachments({
  projectId,
  taskId,
  nameOf,
  t,
  formats,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly nameOf: ReturnType<typeof useMemberNames>;
  readonly t: Translate<"tasks">;
  readonly formats: ReturnType<typeof useFormats>;
}) {
  const attachments = useQuery(taskAttachmentsQueryOptions(projectId, taskId));
  const attach = useAddTaskAttachment(projectId, taskId);
  const [pick, setPick] = useState<{
    readonly file: File;
    readonly key: string;
  } | null>(null);
  const [fraction, setFraction] = useState<number | null>(null);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const run = (chosen: { readonly file: File; readonly key: string }): void => {
    setFraction(0);
    attach.mutate(
      {
        file: chosen.file,
        idempotencyKey: chosen.key,
        onProgress: (progress) => {
          setFraction(progress.fraction);
        },
      },
      {
        onSuccess: () => {
          setPick(null);
          setFraction(null);
        },
        onError: () => {
          setFraction(null);
        },
      },
    );
  };

  const failure = (error: unknown): string => {
    if (error instanceof UploadError) {
      const key = attachFailureKey(error.failure);
      return key === "attachment.rejected" && error.reason !== null
        ? t(key, { reason: error.reason })
        : t(key === "attachment.rejected" ? "attachment.failed" : key);
    }
    return detailOf(error) ?? t(taskErrorKey(error));
  };

  return (
    <section className={styles.section}>
      <SectionTitle as="h3">{t("attachment.title")}</SectionTitle>
      {attachments.isError ? (
        <InlineError variant="block">
          {detailOf(attachments.error) ?? t(taskErrorKey(attachments.error))}
        </InlineError>
      ) : attachments.isPending ? (
        <div className={styles.loadingBlock}>
          <SkeletonLines label={t("status.loading")} lines={2} />
        </div>
      ) : attachments.data.items.length === 0 ? (
        <p className={styles.muted}>{t("attachment.none")}</p>
      ) : (
        <ul className={styles.attachmentList}>
          {attachments.data.items.map((attachment) => (
            <li key={attachment.id} className={styles.attachmentRow}>
              <Button
                size="sm"
                variant="tertiary"
                icon={<Download size={14} strokeWidth={1.5} aria-hidden />}
                onClick={() => {
                  void (async () => {
                    try {
                      const { url } = await readDownloadUrl(
                        attachment.blob_ref_id,
                      );
                      // `window.open` and not a rendered `<a href>`: the URL is signed and
                      // short-lived, so putting it in the DOM ahead of the click would age
                      // it and hand a copyable credential to anything reading the page.
                      window.open(url, "_blank", "noopener,noreferrer");
                    } catch {
                      setDownloadFailed(true);
                    }
                  })();
                }}
              >
                {attachment.original_filename ?? t("attachment.unnamed")}
              </Button>
              <span className={styles.rowMeta}>
                {renderPersonName(
                  t,
                  nameOf(attachment.uploaded_by_user_id),
                  t("person.unavailable"),
                )}
                {" · "}
                {formats.moment(attachment.uploaded_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {downloadFailed ? (
        <InlineError>{t("error.unexpected")}</InlineError>
      ) : null}

      <input
        ref={input}
        type="file"
        className={styles.hiddenInput}
        aria-hidden
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Clearing the value lets the same file be picked again after a cancel — and a
          // re-pick is a new choice, so it mints a new key.
          event.target.value = "";
          if (file === undefined) return;
          const chosen = { file, key: crypto.randomUUID() };
          setPick(chosen);
          run(chosen);
        }}
      />
      {fraction !== null ? (
        <p className={styles.muted}>
          {t("attachment.uploading", { percent: Math.round(fraction * 100) })}
        </p>
      ) : (
        <div className={styles.noticeActions}>
          <Button
            size="sm"
            variant="secondary"
            disabled={attach.isPending}
            onClick={() => {
              input.current?.click();
            }}
          >
            {t("attachment.add")}
          </Button>
          {attach.isError && pick !== null ? (
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => {
                run(pick);
              }}
            >
              {t("attachment.retry")}
            </Button>
          ) : null}
        </div>
      )}
      {attach.isError ? (
        <InlineError variant="block">{failure(attach.error)}</InlineError>
      ) : null}
    </section>
  );
}

function Watchers({
  projectId,
  taskId,
  nameOf,
  t,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly nameOf: ReturnType<typeof useMemberNames>;
  readonly t: Translate<"tasks">;
}) {
  const watchers = useQuery(taskWatchersQueryOptions(projectId, taskId));
  const setWatching = useSetTaskWatching(projectId, taskId);

  return (
    <section className={styles.railCard}>
      <SectionTitle as="h3">{t("watchers.title")}</SectionTitle>
      {watchers.isError ? (
        <InlineError>
          {detailOf(watchers.error) ?? t(taskErrorKey(watchers.error))}
        </InlineError>
      ) : watchers.isPending ? (
        <div className={styles.loadingBlock}>
          <SkeletonLines label={t("status.loading")} lines={3} />
        </div>
      ) : (
        <>
          {watchers.data.items.length === 0 ? (
            <p className={styles.muted}>{t("watchers.none")}</p>
          ) : (
            <ul className={styles.railList}>
              {watchers.data.items.map((watcher) => (
                <li key={watcher.user_id}>
                  {renderPersonName(
                    t,
                    nameOf(watcher.user_id),
                    t("person.unavailable"),
                  )}
                </li>
              ))}
            </ul>
          )}
          <Button
            size="sm"
            variant="secondary"
            loading={setWatching.isPending}
            onClick={() => {
              setWatching.mutate(!watchers.data.watching);
            }}
          >
            {t(watchers.data.watching ? "watchers.stop" : "watchers.watch")}
          </Button>
        </>
      )}
      {setWatching.isError ? (
        <InlineError>
          {detailOf(setWatching.error) ?? t(taskErrorKey(setWatching.error))}
        </InlineError>
      ) : null}
    </section>
  );
}

/**
 * §F's standing record: every firm this task admitted, why, and from when. A display of a
 * fact, not a control — there is no revoke here and none exists server-side; the visibility
 * is the agreed mitigation for admission happening as a side effect of ordinary work.
 *
 * Firm names resolve through the member list's `company_*` labels, because the audience row
 * carries a workspace id and this caller may not read arbitrary workspaces. A firm whose
 * people have all left the project — or a caller the member list refuses — renders as a firm
 * this screen cannot name, which is the honest rendering of what it was told.
 */
function Audience({
  projectId,
  taskId,
  members,
  t,
  formats,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly members: readonly {
    readonly company_workspace_id?: string | null;
    readonly company_name?: string | null;
  }[];
  readonly t: Translate<"tasks">;
  readonly formats: ReturnType<typeof useFormats>;
}) {
  const audience = useQuery(taskAudienceQueryOptions(projectId, taskId));

  const firmName = (workspaceId: string): string =>
    members.find((member) => member.company_workspace_id === workspaceId)
      ?.company_name ?? t("audience.firmUnknown");

  return (
    <section className={styles.railCard}>
      <SectionTitle as="h3">{t("audience.title")}</SectionTitle>
      {audience.isError ? (
        <InlineError>
          {detailOf(audience.error) ?? t(taskErrorKey(audience.error))}
        </InlineError>
      ) : audience.isPending ? (
        <div className={styles.loadingBlock}>
          <SkeletonLines label={t("status.loading")} lines={2} />
        </div>
      ) : audience.data.items.length === 0 ? (
        <p className={styles.muted}>{t("audience.internal")}</p>
      ) : (
        <ul className={styles.railList}>
          {audience.data.items.map((entry) => (
            <li key={`${entry.audience_workspace_id} ${entry.reason}`}>
              <span className={styles.metaValue}>
                {firmName(entry.audience_workspace_id)}
              </span>
              <br />
              <span className={styles.rowMeta}>
                {t(REASON_KEY[entry.reason])}
                {" · "}
                {t("audience.since", { date: formats.date(entry.granted_at) })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
